import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import BusinessHoursCard from '../../components/admin/BusinessHoursCard';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { DAYS_OF_WEEK_PREFERRED_ORDER, TIMEZONES } from '../../constants';
import {
  getBusinessConfigById,
  getEmployeeWorkHours,
  patchBusinessInfoConfig,
  patchFeaturesConfig,
  patchPaymentInfoConfig,
  patchPreferencesConfig,
  rebuildCustomersForSite,
  rebuildStatsForSite,
} from '../../firebase/firebase-delegate';
import { BusinessConfig } from '../../types';
import {
  businessHoursEntriesError,
  BusinessHoursEntry,
  cloneBusinessHoursEntries,
  convertHoursToEntries,
  convertWorkHoursToBusinessEntries,
  DayKey,
  entriesToHoursMap,
} from '../../utils/businessHours';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { updateTenantResourceTerminology } from '../../sitePublishing/api';

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

type BooleanFeatureKey = 'enableCustomerSMS' | 'enableEmployeeSMS' | 'enablePayments';
type BooleanPreferenceKey =
  | 'enableEmployeeEmails'
  | 'requirePaymentForBooking'
  | 'allowTips'
  | 'allowAnyAvailableEmployee';

type FlagRowProps = {
  checked: boolean;
  description: string;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
};

const FlagRow: React.FC<FlagRowProps> = ({ checked, description, disabled, label, onChange }) => (
  <Box sx={{ py: 1.25 }}>
    <FormControlLabel
      control={(
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      )}
      label={label}
      sx={{ m: 0 }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
      {description}
    </Typography>
  </Box>
);

const formatPlanName = (value?: string): string => {
  if (!value) return 'Not selected';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const EditBusinessConfigPage: React.FC = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BusinessConfig | null>(null);
  const [businessHoursEntries, setBusinessHoursEntries] = useState<Record<DayKey, BusinessHoursEntry>>(
    () => convertHoursToEntries()
  );
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [rebuildCustomersDialogOpen, setRebuildCustomersDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig', siteId],
    enabled: !!siteId,
    queryFn: () => getBusinessConfigById(siteId!),
  });

  useEffect(() => {
    if (!businessConfigQuery.data) return;
    const config = businessConfigQuery.data;
    setDraft({
      ...config,
      features: {
        ...config.features,
        enableCustomerSMS: config.features?.enableCustomerSMS ?? false,
        enableEmployeeSMS: config.features?.enableEmployeeSMS ?? false,
        enablePayments: config.features?.enablePayments ?? false,
      },
      paymentInfo: {
        owner: config.paymentInfo?.owner ?? '',
        ...(config.paymentInfo ?? {}),
        skipSubscriptionCheck: config.paymentInfo?.skipSubscriptionCheck ?? false,
      },
      preferences: {
        ...(config.preferences ?? {}),
        enableEmployeeEmails: config.preferences?.enableEmployeeEmails ?? false,
        requirePaymentForBooking: config.preferences?.requirePaymentForBooking ?? false,
        allowTips: config.preferences?.allowTips ?? false,
        allowAnyAvailableEmployee: config.preferences?.allowAnyAvailableEmployee ?? false,
        minimumBookingNoticeHours: config.preferences?.minimumBookingNoticeHours ?? 1,
        uiFlags: {
          ...(config.preferences?.uiFlags ?? {}),
          showEmployeePickerInBooking:
            config.preferences?.uiFlags?.showEmployeePickerInBooking ?? true,
        },
      },
    });
    setBusinessHoursEntries(convertHoursToEntries(config.businessInfo?.businessHours));
  }, [businessConfigQuery.data]);

  const ownerId = draft?.paymentInfo?.owner;
  const ownerWorkHoursQuery = useQuery({
    queryKey: ['siteOwnerWorkHours', ownerId, siteId],
    queryFn: () => getEmployeeWorkHours(ownerId!, siteId!),
    enabled: !!ownerId && !!siteId,
  });
  const ownerWorkHoursEntries = useMemo(
    () => (ownerWorkHoursQuery.data ? convertWorkHoursToBusinessEntries(ownerWorkHoursQuery.data) : null),
    [ownerWorkHoursQuery.data]
  );

  const bookingUx = resolveAdminBookingUxConfig(draft);
  const isResourceMode = bookingUx.bookingModel === 'resource';
  const resourceSingular = bookingUx.terminology.resourceSingular;
  const resourcePlural = bookingUx.terminology.resourcePlural;

  const validate = (): void => {
    if (!draft?.businessInfo.name.trim()) throw new Error('Business name is required.');
    if (!draft.businessInfo.timezone) throw new Error('Timezone is required.');
    if (isResourceMode && (!resourceSingular.trim() || !resourcePlural.trim())) {
      throw new Error('Resource names cannot be blank.');
    }

    const hoursError = businessHoursEntriesError(businessHoursEntries);
    if (hoursError) throw new Error(hoursError);

    const notice = draft.preferences?.minimumBookingNoticeHours ?? 1;
    if (!Number.isInteger(notice) || notice < 0) {
      throw new Error('Minimum booking notice must be a whole number of hours, zero or greater.');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!siteId || !draft) throw new Error('Tenant configuration is missing.');
      validate();

      const paymentsEnabled = draft.features.enablePayments;
      const preferencesPatch: Partial<NonNullable<BusinessConfig['preferences']>> = {
        enableEmployeeEmails: draft.preferences?.enableEmployeeEmails ?? false,
        requirePaymentForBooking:
          paymentsEnabled && (draft.preferences?.requirePaymentForBooking ?? false),
        allowTips: paymentsEnabled && (draft.preferences?.allowTips ?? false),
        minimumBookingNoticeHours: draft.preferences?.minimumBookingNoticeHours ?? 1,
      };
      if (!isResourceMode) {
        preferencesPatch.allowAnyAvailableEmployee = draft.preferences?.allowAnyAvailableEmployee ?? false;
        preferencesPatch.uiFlags = {
          showEmployeePickerInBooking:
            draft.preferences?.uiFlags?.showEmployeePickerInBooking ?? true,
        };
      }

      await Promise.all([
        patchBusinessInfoConfig({
          name: draft.businessInfo.name.trim(),
          timezone: draft.businessInfo.timezone,
          businessHours: entriesToHoursMap(businessHoursEntries),
        }, siteId),
        patchFeaturesConfig({
          enableCustomerSMS: draft.features.enableCustomerSMS,
          enableEmployeeSMS: draft.features.enableEmployeeSMS,
          enablePayments: paymentsEnabled,
        }, siteId),
        patchPreferencesConfig(preferencesPatch, siteId),
        patchPaymentInfoConfig({
          skipSubscriptionCheck: draft.paymentInfo?.skipSubscriptionCheck ?? false,
        }, siteId),
        ...(isResourceMode ? [updateTenantResourceTerminology({
          tenantId: siteId,
          resourceSingular: resourceSingular.trim(),
          resourcePlural: resourcePlural.trim(),
        })] : []),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessConfig', siteId] });
      setSnackbar({ open: true, message: 'Tenant operations updated.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const rebuildStatsMutation = useMutation({
    mutationFn: async () => {
      if (!siteId) throw new Error('Site ID is missing.');
      await rebuildStatsForSite(siteId);
    },
    onSuccess: () => setSnackbar({
      open: true,
      message: 'Analytics stats rebuilt successfully.',
      severity: 'success',
    }),
    onError: (error: Error) => setSnackbar({
      open: true,
      message: error.message || 'Failed to rebuild analytics stats.',
      severity: 'error',
    }),
    onSettled: () => setRebuildDialogOpen(false),
  });

  const rebuildCustomersMutation = useMutation({
    mutationFn: async () => {
      if (!siteId) throw new Error('Site ID is missing.');
      await rebuildCustomersForSite(siteId);
    },
    onSuccess: () => setSnackbar({
      open: true,
      message: 'Customer data rebuilt successfully.',
      severity: 'success',
    }),
    onError: (error: Error) => setSnackbar({
      open: true,
      message: error.message || 'Failed to rebuild customer data.',
      severity: 'error',
    }),
    onSettled: () => setRebuildCustomersDialogOpen(false),
  });

  const updateFeature = (key: BooleanFeatureKey, checked: boolean) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        features: { ...current.features, [key]: checked },
        preferences: key === 'enablePayments' && !checked
          ? { ...current.preferences, requirePaymentForBooking: false, allowTips: false }
          : current.preferences,
      };
    });
  };

  const updatePreference = (key: BooleanPreferenceKey, checked: boolean) => {
    setDraft((current) => current ? ({
      ...current,
      preferences: { ...current.preferences, [key]: checked },
    }) : current);
  };

  if (businessConfigQuery.isLoading) return <LoadingSpinner label="Loading business" />;

  if (businessConfigQuery.isError || !draft) {
    return (
      <Container maxWidth="sm" sx={{ py: 5 }}>
        <Alert severity="error">
          {businessConfigQuery.error instanceof Error
            ? businessConfigQuery.error.message
            : 'This tenant configuration could not be loaded.'}
        </Alert>
      </Container>
    );
  }

  const paymentInfo = draft.paymentInfo;
  const planName = formatPlanName(paymentInfo?.pendingPlanKey ?? paymentInfo?.planType);
  const subscriptionStatus = paymentInfo?.subscriptionStatus ?? 'inactive';
  const paymentsEnabled = draft.features.enablePayments;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Button size="small" onClick={() => navigate(`/admin/superadmin/manage/${siteId}`)} sx={{ mb: 1 }}>
            Back to tenant
          </Button>
          <Typography variant="h3" component="h1" fontWeight={800}>
            Tenant Operations
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Operational controls for {draft.businessInfo.name}. Website content and design now live in Edit Site.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
          <Button
            variant="contained"
            onClick={() => window.location.assign(`/admin/superadmin/site?tenant=${encodeURIComponent(siteId!)}`)}
          >
            Edit Site
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/superadmin/manage/${siteId}/manage-services`)}
          >
            Manage Services
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/superadmin/manage/${siteId}/add-employee`)}
          >
            Add {resourceSingular}
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.location.assign(`/admin/schedule-hub?tenant=${encodeURIComponent(siteId!)}`)}
          >
            Open Tenant Dashboard
          </Button>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" fontWeight={750} gutterBottom>
              Identity and scheduling defaults
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              These legacy hours are also used as schedule defaults for new {resourcePlural.toLowerCase()}. Use Edit Site for normal public-hours changes.
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Business name"
                value={draft.businessInfo.name}
                onChange={(event) => setDraft((current) => current ? ({
                  ...current,
                  businessInfo: { ...current.businessInfo, name: event.target.value },
                }) : current)}
              />
              <TextField
                select
                fullWidth
                label="Timezone"
                value={draft.businessInfo.timezone}
                onChange={(event) => setDraft((current) => current ? ({
                  ...current,
                  businessInfo: { ...current.businessInfo, timezone: event.target.value },
                }) : current)}
              >
                {TIMEZONES.map((timezone) => (
                  <MenuItem key={timezone} value={timezone}>{timezone}</MenuItem>
                ))}
              </TextField>
              {isResourceMode ? <>
                <Divider />
                <Box>
                  <Typography fontWeight={700}>Resource names</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These labels are used throughout scheduling, services, and customer booking.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField fullWidth required label="Singular resource name" value={resourceSingular}
                    placeholder="e.g. Chair"
                    onChange={(event) => setDraft((current) => current ? ({
                      ...current,
                      preferences: {
                        ...(current.preferences ?? {}),
                        terminology: {
                          ...(current.preferences?.terminology ?? {}),
                          resourceSingular: event.target.value,
                        },
                      },
                    }) : current)} />
                  <TextField fullWidth required label="Plural resource name" value={resourcePlural}
                    placeholder="e.g. Chairs"
                    onChange={(event) => setDraft((current) => current ? ({
                      ...current,
                      preferences: {
                        ...(current.preferences ?? {}),
                        terminology: {
                          ...(current.preferences?.terminology ?? {}),
                          resourcePlural: event.target.value,
                        },
                      },
                    }) : current)} />
                </Stack>
              </> : null}
            </Stack>

            <Accordion
              disableGutters
              elevation={0}
              expanded={hoursExpanded}
              onChange={(_, expanded) => setHoursExpanded(expanded)}
              sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={700}>Default schedule hours</Typography>
                  <Typography variant="body2" color="text.secondary">Expand to review or edit all seven days.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {ownerWorkHoursEntries && (
                  <Button
                    variant="outlined"
                    sx={{ mb: 2 }}
                    onClick={() => setBusinessHoursEntries(cloneBusinessHoursEntries(ownerWorkHoursEntries))}
                  >
                    Match owner work hours
                  </Button>
                )}
                {DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => (
                  <BusinessHoursCard
                    key={day}
                    day={day}
                    entry={businessHoursEntries[day]}
                    onChange={(entry) => setBusinessHoursEntries((current) => ({ ...current, [day]: entry }))}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" fontWeight={750}>Quick flags</Typography>

            <Typography variant="overline" color="text.secondary">Notifications</Typography>
            <FlagRow
              label="Customer SMS"
              description="Allow booking and appointment text messages to customers."
              checked={draft.features.enableCustomerSMS}
              onChange={(checked) => updateFeature('enableCustomerSMS', checked)}
            />
            <FlagRow
              label={isResourceMode ? 'Orchestrator SMS' : `${resourceSingular} SMS`}
              description={isResourceMode
                ? 'Send appointment text messages to the primary orchestrator.'
                : `Allow appointment text messages to ${resourcePlural.toLowerCase()}.`}
              checked={draft.features.enableEmployeeSMS}
              onChange={(checked) => updateFeature('enableEmployeeSMS', checked)}
            />
            <FlagRow
              label={isResourceMode ? 'Orchestrator email' : `${resourceSingular} email`}
              description={isResourceMode
                ? 'Send appointment emails to the primary orchestrator.'
                : `Send appointment emails to ${resourcePlural.toLowerCase()}.`}
              checked={draft.preferences?.enableEmployeeEmails ?? false}
              onChange={(checked) => updatePreference('enableEmployeeEmails', checked)}
            />

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Payments</Typography>
            <FlagRow
              label="Card payments"
              description="Allow customers to pay by card during booking."
              checked={paymentsEnabled}
              onChange={(checked) => updateFeature('enablePayments', checked)}
            />
            <FlagRow
              label="Require payment to book"
              description="Do not confirm a customer booking until payment succeeds."
              checked={draft.preferences?.requirePaymentForBooking ?? false}
              disabled={!paymentsEnabled}
              onChange={(checked) => updatePreference('requirePaymentForBooking', checked)}
            />
            <FlagRow
              label="Allow tips"
              description="Let customers add a tip during card checkout."
              checked={draft.preferences?.allowTips ?? false}
              disabled={!paymentsEnabled}
              onChange={(checked) => updatePreference('allowTips', checked)}
            />

            {!isResourceMode && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="overline" color="text.secondary">Booking assignment</Typography>
                <FlagRow
                  label={`Allow any available ${resourceSingular.toLowerCase()}`}
                  description="Offer automatic assignment when more than one eligible option is available."
                  checked={draft.preferences?.allowAnyAvailableEmployee ?? false}
                  onChange={(checked) => updatePreference('allowAnyAvailableEmployee', checked)}
                />
                <FlagRow
                  label={`Show ${resourceSingular.toLowerCase()} picker`}
                  description={`Let customers choose a specific ${resourceSingular.toLowerCase()} while booking.`}
                  checked={draft.preferences?.uiFlags?.showEmployeePickerInBooking ?? true}
                  onChange={(checked) => setDraft((current) => current ? ({
                    ...current,
                    preferences: {
                      ...current.preferences,
                      uiFlags: {
                        ...(current.preferences?.uiFlags ?? {}),
                        showEmployeePickerInBooking: checked,
                      },
                    },
                  }) : current)}
                />
              </>
            )}
            <TextField
              fullWidth
              type="number"
              label="Minimum booking notice (hours)"
              value={draft.preferences?.minimumBookingNoticeHours ?? 1}
              inputProps={{ min: 0, step: 1 }}
              onChange={(event) => setDraft((current) => current ? ({
                ...current,
                preferences: {
                  ...current.preferences,
                  minimumBookingNoticeHours: Number(event.target.value),
                },
              }) : current)}
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" fontWeight={750}>Plan and billing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              Read-only billing context. Capacity and Stripe-managed values are changed through subscription flows, not this form.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`Plan: ${planName}`} />
              <Chip
                label={`Status: ${subscriptionStatus}`}
                color={subscriptionStatus === 'active' || subscriptionStatus === 'trialing' ? 'success' : 'default'}
              />
              <Chip label={`Login seats: ${draft.features.employeeCount ?? 0}`} />
              <Chip label={`Loginless resources: ${draft.features.loginlessEmployeeCount ?? 0}`} />
              <Chip label={`Orchestrator: ${draft.preferences?.orchestratorMode ? 'On' : 'Off'}`} />
              <Chip label={`Stripe customer: ${paymentInfo?.stripeCustomerId ? 'Connected' : 'Not connected'}`} />
            </Stack>

            <Alert severity="warning" sx={{ mt: 2.5 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={paymentInfo?.skipSubscriptionCheck ?? false}
                    onChange={(event) => setDraft((current) => current ? ({
                      ...current,
                      paymentInfo: {
                        owner: current.paymentInfo?.owner ?? '',
                        ...(current.paymentInfo ?? {}),
                        skipSubscriptionCheck: event.target.checked,
                      },
                    }) : current)}
                  />
                )}
                label="Bypass subscription requirement"
                sx={{ m: 0 }}
              />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Founder override only. Turning this off does not activate a subscription; normal billing status takes over.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="h6" fontWeight={750}>Repair tools</Typography>
              <Typography variant="body2" color="text.secondary">Use only when derived tenant data is stale or missing.</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" color="warning" onClick={() => setRebuildDialogOpen(true)}>
                Rebuild analytics
              </Button>
              <Button variant="outlined" color="warning" onClick={() => setRebuildCustomersDialogOpen(true)}>
                Rebuild customers
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="large"
            variant="contained"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            startIcon={saveMutation.isPending ? <CircularProgress color="inherit" size={18} /> : undefined}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save operational settings'}
          </Button>
        </Box>
      </Stack>

      <Dialog open={rebuildDialogOpen} onClose={() => setRebuildDialogOpen(false)}>
        <DialogTitle>Rebuild analytics?</DialogTitle>
        <DialogContent>
          This recalculates derived appointment statistics for this tenant. Existing appointments are not changed.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildDialogOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={rebuildStatsMutation.isPending}
            onClick={() => rebuildStatsMutation.mutate()}
          >
            Rebuild
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rebuildCustomersDialogOpen} onClose={() => setRebuildCustomersDialogOpen(false)}>
        <DialogTitle>Rebuild customer data?</DialogTitle>
        <DialogContent>
          This regenerates customer records from this tenant's appointment history.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildCustomersDialogOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={rebuildCustomersMutation.isPending}
            onClick={() => rebuildCustomersMutation.mutate()}
          >
            Rebuild
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditBusinessConfigPage;
